/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {ColDef} from 'ag-grid-community';
import {IcdTemplatePinCellEditorComponent} from '../cell-renderer/icd-template-pin-cell-editor.component';

/**
 * Shared ICD-template column machinery.
 *
 * The dynamic `template_*` grid columns (one per Program / Model Year / RPO / Build Phase tuple,
 * each cell holding blank, 'X', or a circuit number) are needed by every matrix flavour that feeds
 * ICD creation — currently the IO Matrix ICDs page, next the Aggregator IO Matrix ICDs page. These
 * helpers are deliberately pure functions in their own file rather than methods on
 * `IoMatrixWorkflowComponent`, because that component also owns cadence-merge, variant and
 * snapshot behaviour that only applies to the plain IO Matrix. A page that needs template columns
 * can import these without inheriting any of that.
 */

/** Every dynamic template column id starts with this, which is how rows/columns are recognised. */
export const TEMPLATE_FIELD_PREFIX = 'template_';

/** Allowed quick-pick values in the template pin editor; a circuit number may also be typed/searched. */
export const TEMPLATE_PIN_EDITOR_OPTIONS: string[] = ['', 'X'];

/**
 * One dynamic template column.
 *
 * `variant` is kept required to match the shape callers already declare. Flavours without a variant
 * concept (the aggregator matrix) pass `''`, which is also how the IO Matrix spells "Original".
 */
export interface IcdTemplateColumn {
    field: string;
    program: string;
    modelYear: string;
    rpo: string;
    additionalRpos: string;
    buildPhase: string;
    variant: string;
}

/**
 * Builds the stable column id for a template tuple.
 *
 * `variantSegment` must already be sanitized by the caller: variant identity is flavour-specific
 * (the IO Matrix hashes the raw token so visually similar variants cannot collide), so this helper
 * stays agnostic and simply appends the segment. Flavours without variants omit it.
 */
export function buildTemplateField(program: string, modelYear: string, rpo: string, additionalRpos: string,
                                   buildPhase: string, variantSegment = ''): string {
    const suffix = `${(program || '').trim().toUpperCase()}_${(modelYear || '').trim()}_`
        + `${(rpo || '').trim().toUpperCase()}_${(additionalRpos || '').trim().toUpperCase()}_`
        + `${(buildPhase || '').trim().toUpperCase()}_${variantSegment}`;
    return `${TEMPLATE_FIELD_PREFIX}${suffix.replace(/[^A-Z0-9_]/g, '_')}`;
}

export function isTemplateField(field: string): boolean {
    return !!field && field.startsWith(TEMPLATE_FIELD_PREFIX);
}

/**
 * Normalizes a template cell value: blank stays blank, any casing of 'x' becomes the canonical 'X',
 * and anything else is a trimmed circuit-number token passed through as typed. The backend applies
 * the same rule, so a round-trip never shows a spurious change.
 */
export function normalizeTemplateCircuitSelection(value: unknown): string {
    if (value == null) {
        return '';
    }
    const normalized = String(value).trim();
    if (normalized.toUpperCase() === 'X') {
        return 'X';
    }
    return normalized;
}

/**
 * The editable, green-tinted ColDef for one template column. `singleClickEdit` keeps the X/circuit
 * pick to a single click, which matters when marking many rows in a row.
 */
export function buildTemplateColDef(column: IcdTemplateColumn,
                                    editorOptions: string[] = TEMPLATE_PIN_EDITOR_OPTIONS): ColDef {
    return {
        field: column.field,
        headerName: `${column.program} ${column.modelYear} ${column.rpo} ${column.buildPhase}`,
        editable: true,
        cellClass: 'program-column',
        headerClass: 'program-column-header',
        width: 170,
        minWidth: 140,
        singleClickEdit: true,
        cellEditor: IcdTemplatePinCellEditorComponent,
        cellEditorParams: {
            values: editorOptions
        }
    };
}
